import { GlobalStore } from "@roc/feature-app-core";
import { action, flow, makeObservable, observable } from "mobx";
import { debounce } from "@roc/feature-utils";
import { GeneralTasksFilters, TaskScope, TaskStatus, TaskStatusDB } from "../types/generalTasksTypes";
import { GeneralTasksStore } from "./generalTasksStore";
import { EditTaskStore } from "./editTaskStore";
import { LOAN_SUPPORT, TECH_SUPPORT_ID } from "../utils/generalTasksConstants";
import { GeneralTasksService } from "../services/generalTasksService";
import { cloneObject } from "../utils/generalTasksUtils";

export class SupportTasksStore {
  globalStore: GlobalStore;
  openModal: boolean = false;
  generalTasksStore: GeneralTasksStore;
  editTaskStore: EditTaskStore;
  private generalTaskService: GeneralTasksService;
  unreadCountMessages: number = 0;
  showCompletedInLastThirtyDays: boolean = true;

  filters: Record<string, any>;
  defaultFilters: Record<string, any> = {
    [GeneralTasksFilters.TASK_ID]: '',
    [GeneralTasksFilters.TITLE]: '',
    [GeneralTasksFilters.STATUS]: [{ label: TaskStatus.PENDING, value: TaskStatusDB.PENDING }, { label: TaskStatus.MORE_INFO_NEEDED, value: TaskStatusDB.MORE_INFO_NEEDED }, { label: TaskStatus.BEING_REVIEWED, value: TaskStatusDB.BEING_REVIEWED }],
    [GeneralTasksFilters.TASK_SCOPE]: [{ value: TaskScope.REPORTED_BY_ME }],
  };

  applyFiltersCallback: () => void;
  applyDebouncedFilters = debounce(this.applyFilters.bind(this));

  constructor(globalStore: GlobalStore, generalTasksStore: GeneralTasksStore) {
    this.globalStore = globalStore;
    this.generalTasksStore = generalTasksStore;
    this.editTaskStore = generalTasksStore.editTaskStore;
    this.generalTaskService = new GeneralTasksService();
    this.filters = cloneObject(this.defaultFilters);

    makeObservable(this, {
      openModal: observable,
      unreadCountMessages: observable,
      showCompletedInLastThirtyDays: observable,
      filters: observable,
      setModalOpen: action,
      setFilter: action,
      setshowCompletedInLastThirtyDays: action,
      resetFilters: flow,
      fetchUnreadMessagesCount: flow,
      saveSupportTask: flow,
      getFilterParams: action,
      setApplyFiltersCallback: action,
      applyFilters: action,
    });
  }

  setApplyFiltersCallback(callbackFn: () => void) {
    this.applyFiltersCallback = callbackFn;
  }

  applyFilters() {
    this.applyFiltersCallback?.();
  }

  setFilter(filterName: GeneralTasksFilters, value: any) {
    this.filters[filterName] = value;
    this.applyDebouncedFilters();
  }

  setshowCompletedInLastThirtyDays(value: boolean) {
    this.showCompletedInLastThirtyDays = value;
    this.applyFilters();
  }

  *resetFilters() {
    this.filters = cloneObject(this.defaultFilters);
    this.showCompletedInLastThirtyDays = true;
    this.applyFilters();
  }

  setModalOpen(open: boolean) {
    this.openModal = open;
    this.editTaskStore.setIsFromSupport(open);
  }

  *saveSupportTask() {
    const category = this.editTaskStore.getValue('category');
    const isTechSupportTask = category != LOAN_SUPPORT;
    if (isTechSupportTask) {
      this.editTaskStore.onFieldChange('assigneeId', TECH_SUPPORT_ID);
    }
    yield this.editTaskStore.saveTaskWithMessage(undefined, undefined, isTechSupportTask);
  }

  *fetchUnreadMessagesCount() {
    const response = yield this.generalTaskService.getUserTaskssUnreadCountMessages();
    this.unreadCountMessages = response.data.data;
  }

  getFilterParams() {
    const getDropdownValues = (options: any[]) =>
      options?.length > 0 ? options.map(o => o.value).join(',') : undefined;

    const filters = {
      title: this.filters[GeneralTasksFilters.TITLE],
      taskId: this.filters[GeneralTasksFilters.TASK_ID],
      category: 'TECH_SUPPORT',
    };

    const dropdownFilters = {
      status: getDropdownValues(this.filters[GeneralTasksFilters.STATUS]),
    };

    return { filters, dropdownFilters };
  }
}
